import jwt_decode from 'jwt-decode';
import {
  Subscription,
  DecodedTokenDataType,
  EntitlementsType,
  SubscriptionExpiration,
  StripePlanType,
  SubscriberType
} from 'interfaces/subscription.interface';
import focusBearApi from 'services/axios-config';
import { HTTP_STATS_CODE, LOCAL_STORAGE } from 'constants/general';
import { REQUEST_ERROR_MESSAGE_KEY } from 'constants/error.constant';
import { SUBSCRIPTION_INFO_KEY } from 'constants/routes';
import { getLocalStorage } from 'utils/helpers/storage';
import Endpoints from 'constants/endpoints';
import { store } from 'store';
import {
  updateIsCancelingSubscription,
  updateIsTrialUser
} from 'store/reducer/user/slice';
import { toast } from 'react-toastify';
import { t } from 'services/i18n.service';
import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  updateSubscriptionInfo,
  updateIsFetchingStripPlans,
  updateIsSubscriptionChecking,
  updateSubscriptionStripPans,
  updateEntitlements,
  updateActiveEntitlements,
  updateSubscriptions
} from './slice';

import { ENTITLEMENT } from 'enums';
import { updateCancelSubscriptionModal } from '../modal/slice';
import { captureException } from 'services/posthog.service';
import { getTrialStatus } from 'utils/subscription.util';
import { toastRequestError } from 'utils/api-error.util';
import { setHasDataLoadFailed } from '../general/slice';
import { isApiDataValid, isRecord } from 'utils/api/api-data-validity.util';

export const getStripePlans = createAsyncThunk(
  'subscription/get-stripe-plans',
  async (_, { dispatch }) => {
    try {
      dispatch(updateIsFetchingStripPlans(true));
      const { data } = await focusBearApi.get(
        Endpoints.SUBSCRIPTION_STRIPE_PRODUCTS,
        {
          headers: {
            'Cache-Control': 'private, max-age=86400, must-revalidate' //@Description: 1 day == 86400 seconds
          }
        }
      );
      if (!isApiDataValid(data, isRecord)) {
        dispatch(setHasDataLoadFailed(true));
        dispatch(updateIsFetchingStripPlans(false));
        return;
      }
      const plansWithDefaultPrices = data?.data?.filter(
        (item: StripePlanType) => item.default_price
      );
      dispatch(updateSubscriptionStripPans(plansWithDefaultPrices));
      dispatch(updateIsFetchingStripPlans(false));
    } catch (error) {
      captureException(error);
      dispatch(updateIsFetchingStripPlans(false));
    }
  }
);

const getUserSubscription = async () => {
  const token = getLocalStorage(LOCAL_STORAGE.AUTH_BEARER_TOKEN);
  if (!token) {
    throw new Error(
      "Can't fetch subscription info because there is no access token"
    );
  } else {
    const decodedToken: DecodedTokenDataType = await jwt_decode(token);
    const { data }: { data: SubscriberType } = await focusBearApi.get(
      Endpoints.USER_SUBSCRIPTION
    );
    if (!isApiDataValid(data, isRecord)) {
      // Return null instead of throwing so the caller can surface the shared
      // refresh affordance without breaking the page.
      return null;
    }
    const tokenSubscriptionInfo = decodedToken[SUBSCRIPTION_INFO_KEY];
    const userId = tokenSubscriptionInfo.id;
    const stripeCustomerId = tokenSubscriptionInfo.stripeCustomerId;
    const entitlements = Object.entries(data.entitlements ?? {}).map(
      ([entitlement, value]) => ({
        entitlement: entitlement as ENTITLEMENT,
        ...(value as EntitlementsType)
      })
    );
    const subscriptions: Subscription[] = Object.entries(
      data.subscriptions ?? {}
    ).map(([product_identifier, subscription]) => ({
      ...(subscription as Record<string, unknown>),
      product_identifier,
      expires_date:
        (subscription as { expires_date?: string | null }).expires_date ?? null
    }));
    const activeEntitlements = Object.entries(data.expirations ?? {}).map(
      ([entitlement, expirationInfo]) => ({
        entitlement: entitlement as ENTITLEMENT,
        ...(expirationInfo as SubscriptionExpiration)
      })
    );

    return data.hasActiveSubscription
      ? {
          hasActiveSubscription: true,
          stripeCustomerId,
          userId,
          activeEntitlements,
          entitlements,
          subscriptions
        }
      : {
          hasActiveSubscription: false,
          unsubscribeDetectedAt: null,
          stripeCustomerId,
          userId,
          activeEntitlements,
          entitlements,
          subscriptions
        };
  }
};

export const getSubscriptionInfo = createAsyncThunk(
  'subscription/get_subscription_info',
  async (_, { dispatch }) => {
    try {
      dispatch(updateIsSubscriptionChecking(true));
      const subscription = await getUserSubscription();
      if (!subscription) {
        dispatch(setHasDataLoadFailed(true));
        dispatch(updateIsSubscriptionChecking(false));
        return false;
      }
      const { entitlements, activeEntitlements, subscriptions, ...rest } =
        subscription;
      const { isTrialUser } = getTrialStatus(entitlements, activeEntitlements);

      dispatch(updateIsTrialUser(isTrialUser));
      dispatch(updateSubscriptions(subscriptions));
      dispatch(updateEntitlements(entitlements));
      dispatch(updateActiveEntitlements(activeEntitlements));
      dispatch(updateSubscriptionInfo(rest));
      dispatch(updateIsSubscriptionChecking(false));
      return true;
    } catch (error) {
      captureException(error);
      dispatch(setHasDataLoadFailed(true));
      dispatch(updateIsSubscriptionChecking(false));
      return false;
    }
  }
);

export const cancelSubscription = async ({
  cancel_subscription_reason,
  entitlement_id
}: {
  cancel_subscription_reason: string;
  entitlement_id: string;
}) => {
  try {
    store.dispatch(updateIsCancelingSubscription(true));
    const { status } = await focusBearApi.post(
      Endpoints.STRIPE.CANCEL_SUBSCRIPTION_SESSION,
      {
        cancel_subscription_reason,
        entitlement_id
      }
    );
    store.dispatch(updateIsCancelingSubscription(false));
    if (status === HTTP_STATS_CODE.CREATED) {
      store.dispatch(
        updateCancelSubscriptionModal({
          isCancelSubscriptionModalOpened: false
        })
      );
      store.dispatch(getSubscriptionInfo());
    } else {
      toast.error(t(REQUEST_ERROR_MESSAGE_KEY.DEFAULT));
    }
  } catch (error) {
    toastRequestError(error);
    store.dispatch(updateIsCancelingSubscription(false));
  }
};
